import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { AIAgentEmailGraphContainer } from '../../../../components/AIAgentEmailGraph';
import { useCraftEmailFeedback } from '../../../../features/craft-email-agent/useCraftEmailFeedback';
import { appStore } from '../../../../services/appStore';
import { copyInsight } from '../../../../services/clipboard/copyInsight';
import type { EmailPreview } from '../../../../services/jsonParser/types';
import { StyledChartWrapper, StyledFieldLabel, StyledResultContainer } from '../styles';
import { useAiOutreachTracking } from '../useAiOutreachTracking';

import {
  StyledActionsRow,
  StyledCopyButton,
  StyledEmailBody,
  StyledFeedbackButton,
  StyledFeedbackGroup,
  StyledFieldValue,
} from './styles';

type AIOutreachResultProps = {
  emailPreview: EmailPreview;
  displayBodyBefore?: string;
  displayBodyAfter?: string;
  threadId: string | null;
  messageId: string | null;
};

export const AIOutreachResult = ({
  emailPreview,
  displayBodyBefore = '',
  displayBodyAfter = '',
  threadId,
  messageId,
}: AIOutreachResultProps) => {
  const { t } = useTranslation();
  const tracking = useAiOutreachTracking();
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const { mutate: sendFeedback } = useCraftEmailFeedback();

  const handleFeedback = useCallback(
    (type: 'like' | 'dislike') => {
      tracking.trackFeedback(type);
      const newValue = feedback === type ? null : type;
      setFeedback(newValue);

      if (threadId && messageId && newValue) {
        sendFeedback({ threadId, messageId, liked: newValue === 'like' });
      }
    },
    [feedback, threadId, messageId, sendFeedback, tracking]
  );

  const handleCopy = useCallback(async () => {
    tracking.trackCopy();
    const showToast = () => appStore.setToast({ content: t('insights.email.copied'), icon: 'checked' });

    const chartElement = chartRef.current;
    if (chartElement) {
      setIsCopyLoading(true);
      try {
        await copyInsight(chartElement, displayBodyBefore, displayBodyAfter, emailPreview.subject);
        showToast();
      } finally {
        setIsCopyLoading(false);
      }
    } else {
      const subject = emailPreview.subject ? `Subject: ${emailPreview.subject}\n\n\n` : '';
      const fullText = [subject + displayBodyBefore, displayBodyAfter].filter(Boolean).join('\n\n');
      void navigator.clipboard.writeText(fullText).then(showToast);
    }
  }, [emailPreview.subject, displayBodyBefore, displayBodyAfter, t, tracking]);

  return (
    <StyledResultContainer data-automation="AIOutreachResult">
      <div>
        <StyledFieldLabel>{t('company.overview.ai-outreach.subject', 'Subject')}</StyledFieldLabel>
        <StyledFieldValue>{emailPreview.subject}</StyledFieldValue>
      </div>
      <div>
        <StyledFieldLabel>{t('company.overview.ai-outreach.email_body', 'Email body')}</StyledFieldLabel>
        <StyledEmailBody>
          {displayBodyBefore.split('\n').map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
          {emailPreview.chartJson ? (
            <StyledChartWrapper ref={chartRef}>
              <AIAgentEmailGraphContainer chart={emailPreview.chartJson} />
            </StyledChartWrapper>
          ) : null}
          {displayBodyAfter.split('\n').map((paragraph, i) => (
            <p key={`after-${i}`}>{paragraph}</p>
          ))}
        </StyledEmailBody>
      </div>
      <StyledActionsRow>
        <StyledFeedbackGroup>
          <StyledFeedbackButton
            isActive={feedback === 'like'}
            onClick={() => handleFeedback('like')}
            data-automation="AIOutreachFeedbackLike"
          >
            <SWReactIcons iconName={feedback === 'like' ? 'thumb-up' : 'thumb-up-outline'} size="xs" />
          </StyledFeedbackButton>
          <StyledFeedbackButton
            isActive={feedback === 'dislike'}
            $flip={feedback === 'dislike'}
            onClick={() => handleFeedback('dislike')}
            data-automation="AIOutreachFeedbackDislike"
          >
            <SWReactIcons iconName={feedback === 'dislike' ? 'thumb-down' : 'thumb-down-outline'} size="xs" />
          </StyledFeedbackButton>
        </StyledFeedbackGroup>
        <StyledCopyButton
          onClick={() => void handleCopy()}
          disabled={isCopyLoading}
          isLoading={isCopyLoading}
          data-automation="AIOutreachCopy"
        >
          <SWReactIcons iconName="copy" size="xs" iconColor={colorsPalettes.carbon[0]} />
          {t('common.copy', 'Copy')}
        </StyledCopyButton>
      </StyledActionsRow>
    </StyledResultContainer>
  );
};
